import { css } from '@emotion/react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import moment from 'moment';
import { getSanitizedStyles } from 'shared/utils';
import ProgramHeader from 'athena/components/program-header/program-header';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import MentoringProgramContext, { MentoringProgramHomeUIContext } from 'athena/components/mentoring-program/context';
import ProgramAdminHome from './home/program-admin';
import ParticipantHome from './home/participant';
import ProgramUnavailable, { errorStyles } from './home/participant/program-unavailable';
import { withOverflowFlexGrowClass } from './constants';


const getScrollHeight = (isTablet: boolean, isUnauthorized: boolean) => {
  if (isTablet) return 'unset';
  if (isUnauthorized) return '100%';
  return 'calc(100vh + 200px)';
};

const Root = () => {
  const { isAdmin, isMentee, isMentor } = useMentoringProgramRole();
  const { error, mentoringProgram, countState } = useContext(MentoringProgramContext);
  const [innerScroll, setInnerScroll] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>();
  const [scrollPercentage, setScrollPercentage] = useState(0);

  // The program counts are fetched only once when MentoringProgram mounts, and
  // it stays mounted while navigating to inner pages (e.g. settings), so
  // refresh the counts every time the home page is shown again to pick up any
  // changes made in the meantime. The initial page load is skipped because the
  // first fetch is still in flight at that point (result is null).
  useEffect(() => {
    if (countState.result) {
      countState.refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { endDate, releasedDate } = mentoringProgram || {};
  const isClosed = endDate && moment().isAfter(moment(endDate));
  const isNotReleased = releasedDate && moment().isBefore(moment(releasedDate));
  const isUnauthorized = (isClosed || isNotReleased) && !isAdmin;

  const isTablet = useMediaQuery({
    query: '(max-width: 834px)',
  });

  const styles = css`
    flex: 1;
    overflow: hidden;
    ${innerScroll === 0 && css`
      overflow-y: ${isUnauthorized ? 'hidden' : 'auto'};
    `};

    .scroll-content {
      height: ${getScrollHeight(isTablet, isUnauthorized)};
    }
  `;

  const uiContextValue = {
    withOverflowFlexGrowClass,
  };

  const containerTranslate = (90 / 100) * scrollPercentage;
  const errorContainerStyles = error ? errorStyles(containerTranslate) : '';

  return (
    <MentoringProgramHomeUIContext.Provider value={uiContextValue}>
      <div
        id='mentoring-program-root'
        css={getSanitizedStyles([styles, errorContainerStyles])}
        ref={scrollContainerRef}
        onScroll={() => {
          const { clientHeight, scrollHeight, scrollTop } = scrollContainerRef.current;
          const total = scrollHeight - clientHeight;
          setScrollPercentage((100 / total) * scrollTop);
        }}
      >
        <div className='scroll-content'>
          <ProgramHeader scrollPercentage={scrollPercentage} showGoBack />
          {error && <ProgramUnavailable />}
          {isAdmin && <ProgramAdminHome setInnerScroll={setInnerScroll} scrollPercentage={scrollPercentage} />}
          {(isMentor || isMentee) && <ParticipantHome setInnerScroll={setInnerScroll} scrollPercentage={scrollPercentage} />}
        </div>
      </div>
    </MentoringProgramHomeUIContext.Provider>
  );
};

export default Root;
